// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Ticket, Mail, UserPlus, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';

const SpectatorTicketSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [registration, setRegistration] = useState<any>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<any>(null);
  const [resending, setResending] = useState(false);
  const { user, profile } = useAuth();

  useEffect(() => {
    if (sessionId) {
      fetchRegistration();
    }
  }, [sessionId]);

  const fetchRegistration = async () => {
    try {
      // Find registration by Stripe session ID
      const { data: reg, error: regError } = await supabase
        .from('registrations')
        .select(`
          *,
          events (
            id,
            name,
            start_date,
            end_date,
            venue_name,
            venue_city,
            venue_state
          )
        `)
        .eq('stripe_session_id', sessionId)
        .single();

      if (regError) throw regError;
      
      setRegistration(reg);
      setEvent(reg.events);

      // Fetch purchased tickets
      const { data: purchasedTickets, error: ticketsError } = await supabase
        .from('purchased_tickets')
        .select('*')
        .eq('registration_id', reg.id);

      if (!ticketsError && purchasedTickets) {
        setTickets(purchasedTickets);
      }

    } catch (error) {
      console.error('Error fetching registration:', error);
    } finally {
      setLoading(false);
    }
  };

  const resendTickets = async () => {
    if (!registration || !event) return;
    
    setResending(true);
    try {
      const recipientEmail = registration.registrant_data?.email;
      if (!recipientEmail) {
        toast.error('No email found in registration');
        return;
      }

      // Format event date
      const eventDate = event.start_date 
        ? parseLocalDate(event.start_date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        : 'TBD';

      const eventLocation = [
        event.venue_name,
        event.venue_city,
        event.venue_state
      ].filter(Boolean).join(', ') || 'See event details';

      // Build ticket list HTML
      const ticketListHtml = tickets.map(ticket => `
        <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 12px; text-align: center;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">${ticket.ticket_name}</h3>
          <div style="background: #f9fafb; padding: 12px; border-radius: 4px; margin-bottom: 8px;">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(ticket.qr_code)}" 
                 alt="QR Code" style="width: 150px; height: 150px;" />
          </div>
          <p style="margin: 0; font-family: monospace; color: #6b7280; font-size: 14px;">${ticket.qr_code}</p>
        </div>
      `).join('');

      const emailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #1e40af; color: white; padding: 24px; text-align: center;">
            <h1 style="margin: 0;">Your Tickets Are Ready!</h1>
          </div>
          
          <div style="padding: 24px;">
            <h2 style="color: #1f2937; margin-top: 0;">${event.name}</h2>
            <p style="color: #6b7280;">
              <strong>Date:</strong> ${eventDate}<br/>
              <strong>Location:</strong> ${eventLocation}
            </p>
            
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;" />
            
            <h3 style="color: #1f2937;">Your Tickets</h3>
            <p style="color: #6b7280; font-size: 14px;">Present these QR codes at the gate for entry.</p>
            
            ${ticketListHtml}
            
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;" />
            
            <div style="text-align: center;">
              <p style="color: #6b7280; font-size: 14px;">
                You can also view your tickets online anytime:
              </p>
              <a href="https://recruitlook.com/my-tickets" 
                 style="display: inline-block; background: #1e40af; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
                View My Tickets
              </a>
            </div>
            
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;" />
            
            <p style="color: #9ca3af; font-size: 12px; text-align: center;">
              Order ID: ${registration.id}<br/>
              Questions? Contact us at support@recruitlook.com
            </p>
          </div>
        </div>
      `;

      // Send email via communication-handler
      const { error } = await invokeEdgeFunction('communication-handler', {
        body: {
          type: 'email',
          action: 'send_immediate',
          organization_id: registration.organization_id,
          recipient: { 
            email: recipientEmail,
          },
          content: {
            subject: `Your Spectator Tickets - ${event.name}`,
            body: emailBody,
          },
          metadata: {
            trigger_source: 'manual_resend',
            registration_id: registration.id,
            event_id: registration.event_id,
            registration_type: 'spectator',
          },
        },
      });

      if (error) throw error;

      toast.success('Tickets resent to ' + recipientEmail);
    } catch (error) {
      console.error('Error resending tickets:', error);
      toast.error('Failed to resend tickets');
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="text-lg">Loading your tickets...</div>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-lg mx-auto">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Registration not found. Please check your email for ticket details.</p>
            <Link to="/events">
              <Button className="mt-4">Browse Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Success Header */}
      <Card className="mb-6">
        <CardContent className="pt-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Purchase Complete!</h1>
          <p className="text-muted-foreground">Your spectator tickets for {event?.name} are confirmed.</p>
        </CardContent>
      </Card>

      {/* Tickets */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            Your Tickets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tickets.length > 0 ? (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="border rounded-lg p-4 text-center">
                  <h3 className="font-semibold mb-2">{ticket.ticket_name}</h3>
                  <QRCodeSVG 
                    value={ticket.qr_code} 
                    size={150}
                    className="mx-auto mb-2"
                  />
                  <p className="text-sm text-muted-foreground">{ticket.qr_code}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center">
              Your tickets are being generated. Check your email shortly.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Email Confirmation */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-500 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold">Confirmation Sent</h3>
              <p className="text-muted-foreground text-sm mb-3">
                We've sent your tickets to {registration.registrant_data?.email}
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={resendTickets}
                disabled={resending}
              >
                <Send className="w-4 h-4 mr-2" />
                {resending ? 'Sending...' : 'Resend Tickets'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account CTA - different for logged in vs guest */}
      {user ? (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h3 className="font-semibold text-green-900">Saved to Your Wallet</h3>
                <p className="text-green-800 text-sm mb-3">
                  Your tickets are available in your digital wallet.
                </p>
                <Link to="/my-tickets">
                  <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-100">
                    View My Tickets
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <UserPlus className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900">Save to Digital Wallet</h3>
                <p className="text-blue-800 text-sm mb-3">
                  Create a free account to access your tickets anytime from your digital wallet.
                </p>
                <Link to={`/login?redirect=/my-tickets&email=${encodeURIComponent(registration.registrant_data?.email || '')}`}>
                  <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Back to Events */}
      <div className="text-center mt-6">
        <Link to="/events">
          <Button variant="ghost">Browse More Events</Button>
        </Link>
      </div>
    </div>
  );
};

export default SpectatorTicketSuccess;